import React, { useEffect, useState } from "react";
import { useAuth } from "@/components/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, CheckCircle, XCircle, Users, Crown } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [totalPool, setTotalPool] = useState(0);
  const [drawings, setDrawings] = useState([]);
  const [drawingInProgress, setDrawingInProgress] = useState(false);
  const [winner, setWinner] = useState(null);
  const [tab, setTab] = useState("claims");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [manualEntries, setManualEntries] = useState("1");
  const [manualReason, setManualReason] = useState("");
  const [addingEntry, setAddingEntry] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [claimsRes, entriesRes, drawingsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/claims`, { withCredentials: true }),
        axios.get(`${API}/admin/raffle/all-entries`, { withCredentials: true }),
        axios.get(`${API}/admin/raffle/drawings`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
      ]);
      setClaims(claimsRes.data);
      setParticipants(entriesRes.data.participants);
      setTotalPool(entriesRes.data.total_pool);
      setDrawings(drawingsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/raffle");
      }
    }
  };

  const handleApprove = async (entryId) => {
    try {
      await axios.post(`${API}/admin/claims/${entryId}/approve`, {}, { withCredentials: true });
      toast.success("Claim approved! +2 entries confirmed.");
      fetchData();
    } catch (err) {
      toast.error("Failed to approve");
    }
  };

  const handleReject = async (entryId) => {
    try {
      await axios.post(`${API}/admin/claims/${entryId}/reject`, {}, { withCredentials: true });
      toast.success("Claim rejected.");
      fetchData();
    } catch (err) {
      toast.error("Failed to reject");
    }
  };

  const handleDrawWinner = async () => {
    if (!window.confirm("Are you sure you want to draw a winner? This action is permanent.")) return;
    setDrawingInProgress(true);
    try {
      const res = await axios.post(`${API}/admin/raffle/draw-winner`, {}, { withCredentials: true });
      setWinner(res.data.winner);
      toast.success(`Winner drawn: ${res.data.winner.name || res.data.winner.email}!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to draw winner");
    } finally {
      setDrawingInProgress(false);
    }
  };

  const handleAddEntry = async () => {
    if (!selectedUser || !manualEntries) return;
    setAddingEntry(true);
    try {
      const res = await axios.post(`${API}/admin/raffle/add-entry`, {
        user_id: selectedUser,
        entries: parseInt(manualEntries),
        reason: manualReason || "Manual admin entry"
      }, { withCredentials: true });
      toast.success(res.data.message);
      setSelectedUser("");
      setManualEntries("1");
      setManualReason("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add entry");
    } finally {
      setAddingEntry(false);
    }
  };

  const handleRemoveEntry = async (entryId) => {
    if (!window.confirm("Remove this entry?")) return;
    try {
      await axios.post(`${API}/admin/raffle/remove-entry`, { entry_id: entryId }, { withCredentials: true });
      toast.success("Entry removed");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove entry");
    }
  };

  if (loading || !user) return null;

  const pendingClaims = claims.filter(c => c.status === "pending_review");
  const reviewedClaims = claims.filter(c => c.status !== "pending_review");

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" style={{ backgroundColor: '#0C0C0C' }} data-testid="admin-panel">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-light" style={{ color: '#F0EDE5' }}>
            Admin Panel
          </h1>
          <p className="font-body text-sm mt-1" style={{ color: '#9A9A9A' }}>
            Manage social claims, view entries, and draw the winner.
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="p-5 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
            <p className="font-heading text-2xl" style={{ color: '#F0EDE5' }}>{totalPool}</p>
            <p className="font-body text-xs" style={{ color: '#9A9A9A' }}>Total Entries</p>
          </div>
          <div className="p-5 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
            <p className="font-heading text-2xl" style={{ color: '#F0EDE5' }}>{participants.length}</p>
            <p className="font-body text-xs" style={{ color: '#9A9A9A' }}>Participants</p>
          </div>
          <div className="p-5 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
            <p className="font-heading text-2xl" style={{ color: '#FF5A36' }}>{pendingClaims.length}</p>
            <p className="font-body text-xs" style={{ color: '#9A9A9A' }}>Pending Claims</p>
          </div>
          <div className="p-5 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
            <p className="font-heading text-2xl" style={{ color: '#22c55e' }}>{drawings.length}</p>
            <p className="font-body text-xs" style={{ color: '#9A9A9A' }}>Drawings Done</p>
          </div>
        </div>

        {/* Draw Winner Button */}
        <div className="p-6 border mb-8" style={{ borderColor: '#FF5A36', backgroundColor: '#1A1210' }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-heading text-xl font-light" style={{ color: '#F0EDE5' }}>Draw Winner</h3>
              <p className="font-body text-xs mt-1" style={{ color: '#9A9A9A' }}>
                {totalPool} entries from {participants.length} participants. Weighted random selection.
              </p>
            </div>
            <button
              onClick={handleDrawWinner}
              disabled={drawingInProgress || totalPool === 0}
              className="flex items-center gap-2 px-6 py-3 text-sm font-accent font-semibold tracking-wider uppercase text-white transition-all hover:brightness-110 disabled:opacity-40"
              style={{ backgroundColor: '#FF5A36' }}
              data-testid="draw-winner-btn"
            >
              <Crown size={18} weight="bold" />
              {drawingInProgress ? "Drawing..." : "Draw Winner"}
            </button>
          </div>
          {winner && (
            <div className="mt-4 p-4 border" style={{ borderColor: '#22c55e', backgroundColor: '#0A1A0A' }}>
              <p className="font-body text-sm" style={{ color: '#22c55e' }}>
                Winner: <span className="font-bold">{winner.name || winner.email}</span> ({winner.email})
              </p>
            </div>
          )}
        </div>

        {/* Past Drawings */}
        {drawings.length > 0 && (
          <div className="mb-8 p-6 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
            <h3 className="font-heading text-lg font-light mb-4" style={{ color: '#F0EDE5' }}>Past Drawings</h3>
            <div className="space-y-2">
              {drawings.map((d, i) => (
                <div key={d.id || i} className="flex items-center justify-between py-2 border-b" style={{ borderColor: '#2A2A2A' }}>
                  <div>
                    <p className="font-body text-sm" style={{ color: '#F0EDE5' }}>{d.winner_name || d.winner_email}</p>
                    <p className="font-body text-xs" style={{ color: '#666' }}>{d.winner_email}</p>
                  </div>
                  <p className="font-body text-xs" style={{ color: '#9A9A9A' }}>{new Date(d.drawn_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setTab("claims")}
            className={`px-4 py-2 text-sm font-accent tracking-wide transition-colors ${tab === "claims" ? "border-b-2" : ""}`}
            style={{ color: tab === "claims" ? '#FF5A36' : '#9A9A9A', borderColor: '#FF5A36' }}
            data-testid="tab-claims"
          >
            Social Claims ({pendingClaims.length} pending)
          </button>
          <button
            onClick={() => setTab("participants")}
            className={`px-4 py-2 text-sm font-accent tracking-wide transition-colors ${tab === "participants" ? "border-b-2" : ""}`}
            style={{ color: tab === "participants" ? '#FF5A36' : '#9A9A9A', borderColor: '#FF5A36' }}
            data-testid="tab-participants"
          >
            All Participants ({participants.length})
          </button>
          <button
            onClick={() => setTab("manage")}
            className={`px-4 py-2 text-sm font-accent tracking-wide transition-colors ${tab === "manage" ? "border-b-2" : ""}`}
            style={{ color: tab === "manage" ? '#FF5A36' : '#9A9A9A', borderColor: '#FF5A36' }}
            data-testid="tab-manage"
          >
            Manage Entries
          </button>
        </div>

        {/* Claims Tab */}
        {tab === "claims" && (
          <div className="space-y-3">
            {pendingClaims.length === 0 && (
              <p className="font-body text-sm p-6 text-center" style={{ color: '#666' }}>No pending claims to review.</p>
            )}
            {pendingClaims.map((claim) => (
              <div key={claim.id} className="p-4 border flex items-center justify-between gap-4" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }} data-testid={`claim-${claim.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-body text-sm font-medium truncate" style={{ color: '#F0EDE5' }}>
                    {claim.user_name} ({claim.user_email})
                  </p>
                  <a href={claim.post_link} target="_blank" rel="noopener noreferrer" className="font-body text-xs truncate block hover:underline" style={{ color: '#A8C6C3' }}>
                    {claim.post_link}
                  </a>
                  <p className="font-body text-xs mt-1" style={{ color: '#666' }}>{new Date(claim.created_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleApprove(claim.id)}
                    className="p-2 border transition-colors hover:bg-[#22c55e] hover:border-[#22c55e] hover:text-white"
                    style={{ borderColor: '#2A2A2A', color: '#22c55e' }}
                    data-testid={`approve-${claim.id}`}
                  >
                    <CheckCircle size={18} weight="bold" />
                  </button>
                  <button
                    onClick={() => handleReject(claim.id)}
                    className="p-2 border transition-colors hover:bg-[#ef4444] hover:border-[#ef4444] hover:text-white"
                    style={{ borderColor: '#2A2A2A', color: '#ef4444' }}
                    data-testid={`reject-${claim.id}`}
                  >
                    <XCircle size={18} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
            {reviewedClaims.length > 0 && (
              <>
                <h4 className="font-body text-xs uppercase tracking-wide mt-8 mb-2" style={{ color: '#666' }}>Previously Reviewed</h4>
                {reviewedClaims.map((claim) => (
                  <div key={claim.id} className="p-4 border flex items-center justify-between opacity-60" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
                    <div>
                      <p className="font-body text-sm" style={{ color: '#F0EDE5' }}>{claim.user_name} ({claim.user_email})</p>
                      <p className="font-body text-xs" style={{ color: '#666' }}>{claim.post_link}</p>
                    </div>
                    <span className={`font-accent text-xs font-bold uppercase ${claim.status === 'confirmed' ? 'text-green-500' : 'text-red-500'}`}>
                      {claim.status === 'confirmed' ? 'Approved' : 'Rejected'}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Participants Tab */}
        {tab === "participants" && (
          <div className="space-y-2">
            {participants.length === 0 && (
              <p className="font-body text-sm p-6 text-center" style={{ color: '#666' }}>No participants yet.</p>
            )}
            {participants.sort((a, b) => b.total_entries - a.total_entries).map((p, i) => (
              <div key={p.user_id} className="p-4 border flex items-center justify-between" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
                <div className="flex items-center gap-3">
                  <span className="font-accent text-xs w-6" style={{ color: '#666' }}>#{i + 1}</span>
                  <div>
                    <p className="font-body text-sm" style={{ color: '#F0EDE5' }}>{p.name || "No name"}</p>
                    <p className="font-body text-xs" style={{ color: '#666' }}>{p.email}</p>
                  </div>
                </div>
                <span className="font-heading text-lg" style={{ color: '#FF5A36' }}>{p.total_entries} entries</span>
              </div>
            ))}
          </div>
        )}

        {/* Manage Entries Tab */}
        {tab === "manage" && (
          <div className="space-y-6">
            {/* Add Entry Form */}
            <div className="p-6 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
              <h3 className="font-heading text-lg font-light mb-4" style={{ color: '#F0EDE5' }}>Add Entries</h3>
              <div className="space-y-3">
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-4 py-3 text-sm font-body border outline-none focus:border-[#FF5A36] transition-colors"
                  style={{ backgroundColor: '#0C0C0C', borderColor: '#2A2A2A', color: '#F0EDE5' }}
                  data-testid="select-user"
                >
                  <option value="">Select a user...</option>
                  {users.map(u => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name || u.email} ({u.email})
                    </option>
                  ))}
                </select>
                <div className="flex gap-3">
                  <input
                    type="number"
                    min="1"
                    value={manualEntries}
                    onChange={(e) => setManualEntries(e.target.value)}
                    placeholder="Entries"
                    className="w-24 px-4 py-3 text-sm font-body border outline-none focus:border-[#FF5A36] transition-colors"
                    style={{ backgroundColor: '#0C0C0C', borderColor: '#2A2A2A', color: '#F0EDE5' }}
                    data-testid="manual-entries-count"
                  />
                  <input
                    type="text"
                    value={manualReason}
                    onChange={(e) => setManualReason(e.target.value)}
                    placeholder="Reason (optional)"
                    className="flex-1 px-4 py-3 text-sm font-body border outline-none focus:border-[#FF5A36] transition-colors"
                    style={{ backgroundColor: '#0C0C0C', borderColor: '#2A2A2A', color: '#F0EDE5' }}
                    data-testid="manual-entries-reason"
                  />
                </div>
                <button
                  onClick={handleAddEntry}
                  disabled={addingEntry || !selectedUser}
                  className="px-6 py-3 text-sm font-accent font-semibold tracking-wider uppercase text-white transition-all hover:brightness-110 disabled:opacity-40"
                  style={{ backgroundColor: '#FF5A36' }}
                  data-testid="add-entry-btn"
                >
                  {addingEntry ? "Adding..." : "Add Entries"}
                </button>
              </div>
            </div>

            {/* All Entries with Remove */}
            <div className="p-6 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
              <h3 className="font-heading text-lg font-light mb-4" style={{ color: '#F0EDE5' }}>All Entries (click to remove)</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {participants.flatMap(p => 
                  (p.entries || []).map(entry => ({ ...entry, user_email: p.email, user_name: p.name }))
                ).length === 0 && (
                  <p className="font-body text-sm text-center" style={{ color: '#666' }}>No entries to manage.</p>
                )}
                {participants.flatMap(p => 
                  (p.entries || []).map(entry => ({ ...entry, user_email: p.email, user_name: p.name }))
                ).map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-3 border" style={{ borderColor: '#1A1A1A', backgroundColor: '#0C0C0C' }}>
                    <div>
                      <p className="font-body text-xs" style={{ color: '#F0EDE5' }}>
                        {entry.user_name || entry.user_email} — <span style={{ color: '#FF5A36' }}>+{entry.entries}</span> ({entry.type}{entry.reason ? `: ${entry.reason}` : ''})
                      </p>
                      <p className="font-body text-xs" style={{ color: '#666' }}>{new Date(entry.created_at).toLocaleDateString()}</p>
                    </div>
                    <button
                      onClick={() => handleRemoveEntry(entry.id)}
                      className="p-1.5 border transition-colors hover:bg-[#ef4444] hover:border-[#ef4444] hover:text-white"
                      style={{ borderColor: '#2A2A2A', color: '#ef4444' }}
                      data-testid={`remove-entry-${entry.id}`}
                    >
                      <XCircle size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
